import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { ModelViewerElement } from "@google/model-viewer/lib/model-viewer";
import {
  isLoggedIn,
  LogSentryException,
  useRemoteConfig,
  useRemoteConfigContext,
} from "@shapr3d/frontend-lib";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Helmet } from "react-helmet";
import { useParams } from "react-router-dom";

import { CommentCarousel } from "../../components/CommentCarousel/CommentCarousel";
import { CommentSidebar } from "../../components/CommentSidebar/CommentSidebar";
import ModelViewerHeader from "../../components/Headers/ModelViewerHeader/ModelViewerHeader";
import ModelViewerNavbar from "../../components/ModelViewerNavbar/ModelViewerNavbar";
import ModelViewerOverlay from "../../components/ModelViewerOverlay/ModelViewerOverlay";
import { ProjectSidebar } from "../../components/ProjectSidebar/ProjectSidebar";
import { localStorageKeys } from "../../config";
import { ShareModalContextProvider } from "../../contexts/ShareModalFormContext";
import useNavigation from "../../hooks/navigation";
import { useTracker } from "../../hooks/useTracker";
import { useWebExport, webExportQueryKey } from "../../hooks/useWebExport";
import { useWindowSize, ViewportType } from "../../hooks/useWindowSize";
import { ElementClickAction } from "../../lib/TrackingAdapter";
import { SyncRequestClient } from "../../stores/requestClient";
import { TemporarySalesBanner } from "./TemporarySalesBanner";

import style from "./ModelViewerPage.module.css";

const DrawingViewer = React.lazy(() => import("../../components/DrawingViewer/DrawingViewer"));
const ModelViewer = React.lazy(() => import("../../components/ModelViewer/ModelViewer"));

const modelFileQueryKey = "modelFile";

export default function ModelViewerPage() {
  return (
    <>
      <Helmet>
        <title>Shapr3D - Published Version</title>
        <meta
          name="description"
          content="Review designs together. View models and add comments directly to your 3D design."
        />
      </Helmet>
      <ModelViewerPageComponent />
    </>
  );
}

function ModelViewerPageComponent() {
  const tracker = useTracker();
  const { isLoading: isRemoteConfigLoading } = useRemoteConfigContext();
  const isNewProjectPage = useRemoteConfig("web_new_project_page-ff");
  const { viewLinkId = "", drawingId, projectId } = useParams();

  const modelViewerRef = useRef<ModelViewerElement>(null);
  const queryClient = useQueryClient();

  const { viewportType } = useWindowSize();

  const [isNavbarOpen, setIsNavbarOpen] = useState(viewportType !== ViewportType.Mobile);
  const handleNavbarClick = useCallback(
    (isOpen: boolean) => {
      setIsNavbarOpen(isOpen);
      const isOpenText = isOpen ? "open" : "close";
      tracker.trackButtonClick({
        elementId: `navbar-${isOpenText}-button`,
        action: isOpen ? ElementClickAction.NAVBAR_OPEN : ElementClickAction.NAVBAR_CLOSE,
      });
    },
    [tracker]
  );

  const [isModelLoaded, setIsModelLoaded] = useState(false);
  const [isDrawingLoaded, setIsDrawingLoaded] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(!isLoggedIn());

  useEffect(() => {
    if (viewportType === ViewportType.Tablet && isSidebarOpen) {
      setIsNavbarOpen(false);
    }
  }, [viewportType, isSidebarOpen]);

  useEffect(() => {
    if (viewportType === ViewportType.Tablet && isNavbarOpen) {
      setIsSidebarOpen(false);
    }
  }, [viewportType, isNavbarOpen]);

  const onModelViewerLoadHandler = useCallback(() => {
    setIsModelLoaded(true);
  }, []);

  const {
    data: webExportData,
    isLoading: isWebExportLoading,
    isSuccess: isWebExportSuccess,
  } = useWebExport();

  useEffect(() => {
    setIsDrawingLoaded(false);
    setIsDrawingViewerError(false);
    setDrawingsProgress(0);
  }, [drawingId]);

  const onDrawingViewerLoadHandler = useCallback(() => {
    setIsDrawingLoaded(true);
  }, []);

  const onDrawingLoadProgressHandler = useCallback((progress: number) => {
    setDrawingsProgress(progress);
  }, []);

  const onDrawingViewerErrorHandler = useCallback(() => {
    setIsDrawingViewerError(true);
  }, []);

  const [isModelViewerError, setIsModelViewerError] = useState(false);
  const onModelViewerErrorHandler = useCallback(() => {
    if (!isModelViewerError) {
      tracker.trackModelDisplayFailed({
        webExportId: webExportData?.webExport?.webExportId,
        viewLinkId,
      });
      localStorage.removeItem(localStorageKeys.modelLoadingCrashFlag(viewLinkId));
      setIsModelViewerError(true);
    }
  }, [tracker, isModelViewerError, webExportData?.webExport?.webExportId, viewLinkId]);

  const onDownloadPdfHandler = useCallback(
    async (drawingId: string) => {
      const drawingToDownload = webExportData?.webExport.drawingsDownload?.find(
        (drawing) => drawing.drawingId === drawingId
      );
      if (drawingToDownload?.download.url) {
        const res = await SyncRequestClient.publicRequest({
          method: "GET",
          url: drawingToDownload?.download.url,
        });
        const blob = new Blob([await res.blob()]);
        const blobURL = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobURL;
        link.download = `${drawingToDownload.title}.pdf`;
        link.click();
        window.URL.revokeObjectURL(blobURL);
      }
    },
    [webExportData]
  );

  const [isDrawingViewerError, setIsDrawingViewerError] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [processProgress, setProcessProgress] = useState(0);
  const [drawingsProgress, setDrawingsProgress] = useState(0);
  const totalProgress = drawingId
    ? drawingsProgress
    : Math.round(downloadProgress * 0.9 + processProgress * 0.1);
  const isLoaded = drawingId ? isDrawingLoaded : isModelLoaded;

  const [modelLocalURL, setModelLocalURL] = useState("");
  useEffect(() => {
    return () => {
      URL.revokeObjectURL(modelLocalURL);
    };
  }, [modelLocalURL]);

  // This invalidate the query for the model file download URL and avoid have all models in the cache
  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: [modelFileQueryKey, webExportData?.webExport?.modelFileDownload?.url],
    });
  }, [queryClient, webExportData?.webExport?.modelFileDownload?.url]);

  const modelDownloadUrl = webExportData?.webExport?.modelFileDownload?.url;
  const [showFileSizeWarning, setShowFileSizeWarning] = useState(false);
  const [userAcknowledgedWarning, setUserAcknowledgedWarning] = useState(false);
  const fileSizeWarningThresholdByteFlag = useRemoteConfig("web_wv_file_size_threshold_byte-rc");
  const fileSizeThresholdByte = Number(fileSizeWarningThresholdByteFlag);
  const modelFileSize = webExportData?.webExport?.fileSize;

  const [modelCrashFlagFound, setModelCrashFlagFound] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    const crashedViewLinkId = localStorage.getItem(
      localStorageKeys.modelLoadingCrashFlag(viewLinkId)
    );
    setModelCrashFlagFound(crashedViewLinkId === viewLinkId);
  }, [viewLinkId]);

  const shouldWarnAboutModelSizeByFileSize =
    !isNaN(fileSizeThresholdByte) &&
    fileSizeThresholdByte > 0 &&
    modelFileSize !== undefined &&
    modelFileSize > fileSizeThresholdByte;
  const shouldWarnAboutModelSize =
    modelCrashFlagFound === true || shouldWarnAboutModelSizeByFileSize;

  const modelDownloadEnabled =
    Boolean(modelDownloadUrl) &&
    modelCrashFlagFound !== undefined &&
    webExportData !== undefined &&
    fileSizeWarningThresholdByteFlag !== undefined &&
    (!shouldWarnAboutModelSize || userAcknowledgedWarning);

  useEffect(() => {
    if (userAcknowledgedWarning) {
      setShowFileSizeWarning(false);
      return;
    }
    setShowFileSizeWarning(shouldWarnAboutModelSize);
  }, [shouldWarnAboutModelSize, userAcknowledgedWarning]);

  useEffect(() => {
    if (showFileSizeWarning && webExportData?.webExport?.webExportId) {
      tracker.trackComplexModelWarningShown({
        viewLinkId,
        webExportId: webExportData.webExport.webExportId,
      });
    }
  }, [showFileSizeWarning, tracker, viewLinkId, webExportData?.webExport?.webExportId]);

  const downloadStartTimeRef = useRef(0);
  const { isError: isModelDownloadError } = useQuery({
    queryKey: [modelFileQueryKey, modelDownloadUrl],
    queryFn: async () => {
      try {
        downloadStartTimeRef.current = performance.now();
        tracker.trackModelDownloadStarted({
          viewLinkId: viewLinkId,
          webExportId: webExportData?.webExport?.webExportId,
        });
        localStorage.setItem(localStorageKeys.modelLoadingCrashFlag(viewLinkId), viewLinkId);

        const response = await SyncRequestClient.publicRequest({
          method: "GET",
          url: `${modelDownloadUrl}`,
        });
        const blob = await response.blob((progress: number) => {
          setDownloadProgress(progress);
        });

        tracker.trackModelDownloaded({
          durationMs: Math.round(performance.now() - downloadStartTimeRef.current),
          viewLinkId,
          webExportId: webExportData?.webExport.webExportId,
        });

        const url = URL.createObjectURL(blob);
        setModelLocalURL(url);

        return url;
      } catch (error) {
        tracker.trackModelDownloadFailed({
          webExportId: webExportData?.webExport.webExportId,
          viewLinkId,
        });
        localStorage.removeItem(localStorageKeys.modelLoadingCrashFlag(viewLinkId));
        LogSentryException({ messagePrefix: "Model download error", error });
        throw error;
      }
    },
    enabled: modelDownloadEnabled,
  });

  const reload = useCallback(async () => {
    setModelLocalURL("");
    setDownloadProgress(0);
    setProcessProgress(0);
    setIsModelViewerError(false);
    setIsModelLoaded(false);
    setShowFileSizeWarning(false);
    setUserAcknowledgedWarning(false);

    setIsDrawingViewerError(false);
    setIsDrawingLoaded(false);
    setDrawingsProgress(0);

    queryClient.invalidateQueries({ queryKey: webExportQueryKey });
  }, [queryClient]);

  const modelTitle = webExportData?.webExport?.title ?? "";

  const drawing = useMemo(
    () =>
      webExportData?.webExport?.drawingsDownload?.find?.(
        (drawing) => drawing.drawingId === drawingId
      ),
    [webExportData?.webExport?.drawingsDownload, drawingId]
  );
  const isDrawingsLayout = useMemo(
    () => Boolean(webExportData?.webExport?.drawingsDownload?.length),
    [webExportData?.webExport?.drawingsDownload]
  );

  const navigate = useNavigation();
  useEffect(() => {
    if (drawingId !== undefined && !drawing && isWebExportSuccess) {
      navigate.goToViewLink({
        viewLinkId,
        projectId,
        replace: false,
        keepHash: true,
      });
    }
  }, [drawingId, isWebExportSuccess, drawing, viewLinkId, projectId, navigate]);

  return (
    <ShareModalContextProvider>
      <div className={style.container}>
        <ModelViewerHeader
          hideARButton={isWebExportLoading}
          disableARButton={!isModelLoaded}
          modelViewer={modelViewerRef}
          title={modelTitle}
          isDrawingsLayout={isDrawingsLayout}
          downloadPdf={onDownloadPdfHandler}
          teamId={webExportData?.teamId}
        />
        <div className={style.modelViewerContainer}>
          {!isLoaded && (
            <ModelViewerOverlay
              progress={totalProgress}
              error={
                (drawingId === undefined && (isModelDownloadError || isModelViewerError)) ||
                (drawingId !== undefined && isDrawingViewerError)
              }
              onRetry={reload}
              showModelSizeWarning={showFileSizeWarning}
              onDismissWarning={() => {
                setShowFileSizeWarning(false);
                setUserAcknowledgedWarning(true);
              }}
              warningBackground={webExportData?.revision?.thumbnailDownloads?.light?.url}
            />
          )}
          {!isRemoteConfigLoading && (
            <>
              {isNewProjectPage && (
                <ProjectSidebar
                  isOpen={isNavbarOpen}
                  setOpen={handleNavbarClick}
                  isCommentingEnabled={Boolean(webExportData?.isCommentingEnabled) && isLoaded}
                />
              )}

              {!isNewProjectPage && (
                <ModelViewerNavbar
                  open={isNavbarOpen && isDrawingsLayout}
                  setOpen={handleNavbarClick}
                  webExport={webExportData?.webExport}
                  isDrawingsLayout={isDrawingsLayout}
                  isInDrawing={Boolean(drawingId)}
                  isCommentingEnabled={Boolean(webExportData?.isCommentingEnabled) && isLoaded}
                />
              )}
            </>
          )}

          {(!drawingId || !isDrawingsLayout) && (
            <>
              <Suspense>
                <ModelViewer
                  ref={modelViewerRef}
                  src={modelLocalURL}
                  modelDownloadURL={webExportData?.webExport?.modelFileDownload?.url}
                  onLoad={onModelViewerLoadHandler}
                  onError={onModelViewerErrorHandler}
                  onLoadProgress={(progress) => setProcessProgress(progress)}
                  isCommentingEnabled={webExportData?.isCommentingEnabled}
                />
              </Suspense>

              {viewportType !== ViewportType.Mobile &&
                (webExportData?.isCommentingEnabled || !isLoggedIn()) && (
                  <CommentSidebar isOpen={isSidebarOpen} setIsOpen={setIsSidebarOpen} />
                )}
              {viewportType === ViewportType.Mobile && webExportData?.isCommentingEnabled && (
                <CommentCarousel />
              )}
            </>
          )}
          {drawingId && isDrawingsLayout && drawing && (
            <Suspense>
              <DrawingViewer
                key={drawing.download.url}
                drawing={drawing}
                onLoad={onDrawingViewerLoadHandler}
                onLoadProgress={onDrawingLoadProgressHandler}
                onError={onDrawingViewerErrorHandler}
              ></DrawingViewer>
            </Suspense>
          )}
          <TemporarySalesBanner />
        </div>
      </div>
    </ShareModalContextProvider>
  );
}
